'use client';

import Link from 'next/link';
import { useTranslations, useFormatter } from 'next-intl';
import { Users, Search } from 'lucide-react';
import { type PaginatedResponse, type LeaderboardTeam } from '@impact/types';
import { formatPublicCurrency } from '@/lib/format/currency-display';
import { Flag } from '@/components/ui/flag/flag';
import { RankBadge } from './RankBadge';

export function TeamLeaderboardTable({
  data,
  isLoading,
  currency,
  fallbackImageUrl,
  campaignName,
}: {
  data: PaginatedResponse<LeaderboardTeam> | undefined;
  isLoading: boolean;
  currency: string;
  fallbackImageUrl?: string | null;
  campaignName?: string | null;
}) {
  const t = useTranslations('leaderboard');
  const format = useFormatter();

  if (isLoading) {
    return (
      <div className="divide-y divide-stone-100">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="p-4 md:px-6 md:py-4 animate-pulse">
            {/* Mobile skeleton */}
            <div className="flex items-center gap-3 md:hidden min-h-[60px]">
              <div className="w-8 h-8 rounded-full bg-stone-200 flex-shrink-0" />
              <div className="w-10 h-10 rounded-lg bg-stone-200 flex-shrink-0" />
              <div className="flex-1 min-w-0 space-y-1.5">
                <div className="h-4 w-28 bg-stone-200 rounded" />
                <div className="h-3 w-20 bg-stone-100 rounded" />
              </div>
              <div className="text-right space-y-1.5 flex-shrink-0">
                <div className="h-5 w-16 bg-stone-200 rounded ml-auto" />
                <div className="h-3 w-12 bg-stone-100 rounded ml-auto" />
              </div>
            </div>
            {/* Desktop skeleton */}
            <div className="hidden md:grid md:grid-cols-12 md:gap-4 md:items-center">
              <div className="col-span-1">
                <div className="w-8 h-8 rounded-full bg-stone-200" />
              </div>
              <div className="col-span-3 flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-stone-200" />
                <div className="space-y-2">
                  <div className="h-4 w-32 bg-stone-200 rounded" />
                  <div className="h-3 w-20 bg-stone-100 rounded" />
                </div>
              </div>
              <div className="col-span-2">
                <div className="h-4 w-24 bg-stone-100 rounded" />
              </div>
              <div className="col-span-2">
                <div className="h-4 w-24 bg-stone-100 rounded" />
              </div>
              <div className="col-span-2 text-right">
                <div className="h-4 w-10 bg-stone-100 rounded ml-auto" />
              </div>
              <div className="col-span-2 text-right">
                <div className="h-6 w-16 bg-stone-200 rounded ml-auto" />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  const items = data?.items ?? [];

  if (items.length === 0) {
    return (
      <div className="p-12 text-center">
        <Search className="w-12 h-12 text-stone-300 mx-auto mb-4" />
        {campaignName ? (
          <p className="text-stone-600">{t('notActiveForCampaign', { name: campaignName })}</p>
        ) : (
          <>
            <p className="text-stone-600">{t('noResults')}</p>
            <p className="text-stone-400 text-sm mt-1">{t('tryDifferentSearch')}</p>
          </>
        )}
      </div>
    );
  }

  return (
    <>
      <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-3 bg-stone-50 text-stone-500 text-sm font-medium border-b border-stone-200">
        <div className="col-span-1">{t('colRank')}</div>
        <div className="col-span-3">{t('colTeam')}</div>
        <div className="col-span-2">{t('colCountry')}</div>
        <div className="col-span-2">{t('colEvent')}</div>
        <div className="col-span-2 text-right">{t('colMembers')}</div>
        <div className="col-span-2 text-right">{t('colRaised')}</div>
      </div>
      <div className="divide-y divide-stone-100">
        {items.map((team) => (
          <Link
            key={team.id}
            href={team.handle ? `/team/${team.handle}` : '#'}
            className="block p-4 md:px-6 md:py-4 hover:bg-stone-50 active:bg-stone-100 transition-colors"
          >
            {/* Mobile card layout */}
            <div className="md:hidden">
              <div className="flex items-center gap-3 min-h-[60px]">
                <RankBadge rank={team.rank} />
                <div className="w-10 h-10 rounded-lg bg-stone-100 overflow-hidden flex items-center justify-center flex-shrink-0">
                  {team.logoUrl || fallbackImageUrl ? (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                      src={(team.logoUrl || fallbackImageUrl)!}
                      alt={team.name}
                      className="object-cover w-full h-full"
                    />
                  ) : (
                    <Users className="w-5 h-5 text-stone-500" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-stone-900 font-medium truncate text-sm">{team.name}</p>
                  <p className="text-stone-500 text-xs truncate">{team.countryName}</p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-base font-bold text-dgd-gold-hover">
                    {formatPublicCurrency(format, team.raised, currency)}
                  </p>
                  <p className="text-stone-500 text-xs">
                    {team.members} {t('colMembers').toLowerCase()}
                  </p>
                </div>
              </div>
            </div>

            {/* Desktop layout */}
            <div className="hidden md:grid md:grid-cols-12 md:gap-4 md:items-center">
              <div className="col-span-1 flex items-center gap-2">
                <RankBadge rank={team.rank} />
              </div>
              <div className="col-span-3 flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-stone-100 overflow-hidden flex items-center justify-center flex-shrink-0">
                  {team.logoUrl || fallbackImageUrl ? (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                      src={(team.logoUrl || fallbackImageUrl)!}
                      alt={team.name}
                      className="object-cover w-full h-full"
                    />
                  ) : (
                    <Users className="w-5 h-5 text-stone-500" />
                  )}
                </div>
                <div className="min-w-0">
                  <p className="text-stone-900 font-medium truncate">{team.name}</p>
                  {team.handle && <p className="text-stone-500 text-sm">@{team.handle}</p>}
                </div>
              </div>
              <div className="col-span-2 flex items-center gap-2">
                <Flag code={team.countryCode} size="sm" />
                <p className="text-stone-600 text-sm">{team.countryName}</p>
              </div>
              <div className="col-span-2">
                <p className="text-stone-600 text-sm truncate">{team.eventName}</p>
              </div>
              <div className="col-span-2 text-right">
                <p className="text-stone-600">{team.members}</p>
              </div>
              <div className="col-span-2 text-right">
                <p className="text-xl font-bold text-dgd-gold-hover">
                  {formatPublicCurrency(format, team.raised, currency)}
                </p>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </>
  );
}
